import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  mdiAccountOutline,
  mdiEmailOutline,
  mdiInformationOutline,
  mdiPhoneOutline
} from "@mdi/js";
import Icon from "@mdi/react";
import * as Yup from "yup";

import Button from "components/Button";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Header from "components/Header";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import Radio from "components/Radio";
import { routes } from "constants/routes";
import { formatPhoneNumber } from "helpers/formatPhoneNumber";
import { faxRegex, phoneRegex } from "helpers/regex";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { AddressInfo, IUserProfileForm, UserAccount, UserProfileFormData } from "models/User";
import { AppDispatch, RootState } from "store";
import { updateUserProfile } from "store/user/actions";
import styles from "./style.module.scss";

const userProfileFormSchema = Yup.object<Partial<IUserProfileForm>>()
  .shape({
    mc: Yup.string().nullable(),
    dot: Yup.string().nullable(),
    contact_phone: Yup.string()
      .nullable()
      .max(19, "Phone number limit exceeded")
      .matches(phoneRegex, { message: "Phone number is not valid", excludeEmptyString: true }),
    fax_number: Yup.string()
      .nullable()
      .max(19, "Phone number limit exceeded")
      .matches(faxRegex, { message: "Fax number is not valid", excludeEmptyString: true }),
    url: Yup.string().url("Url is not valid").nullable(),
    cargo_insurance_value: Yup.string().required("This field is required")
  })
  .test("at-least-one", "Please enter an MC or DOT", function (value) {
    const { mc, dot } = value;
    const isValid = !!mc || !!dot;
    if (!isValid) {
      return this.createError({ path: "mc", message: "Please enter an MC # or DOT #" });
    }
    return true;
  });

export default function UserSettingProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { account } = useSelector((state: RootState) => state.authState);

  const { userDetails } = useSelector((state: RootState) => state.userState);

  const {
    email,
    contact_name,
    contact_phone,
    fax_number,
    url,
    dba,
    address_object,
    mc,
    dot,
    cargo_insurance_provider,
    cargo_insurance_value,
    language: defaultLanguage,
    about
  } = useMemo<Partial<UserAccount>>(() => {
    if (userDetails) return userDetails;
    else {
      return {};
    }
  }, [userDetails]);

  const {
    control,
    register,
    setValue,
    formState: { errors },
    reset,
    watch,
    handleSubmit
  } = useForm<Partial<IUserProfileForm>>({
    resolver: yupResolver(userProfileFormSchema) as any
  });

  const selectedLang = watch("language");

  const goProfile = () => {
    navigate(`${routes.USER}/${userDetails.id}`);
  };

  const onSubmit = async (formData: IUserProfileForm) => {
    const {
      owners_first_name,
      owners_last_name,
      contact_phone,
      fax_number,
      url,
      dba,
      addressData,
      mc,
      dot,
      cargo_insurance_provider,
      cargo_insurance_value,
      about,
      language
    } = formData;

    const userProfileData: { type: string; userId: string } & UserProfileFormData = {
      type: account.type,
      userId: account[account.type as keyof typeof account],
      contact_name: owners_first_name + " " + owners_last_name,
      contact_phone,
      fax_number,
      url,
      dba,
      address: addressData.address ?? "",
      city: addressData.city ?? "",
      state: addressData.state ?? "",
      country: addressData.country ?? "",
      zip: addressData.zip ?? "",
      mc,
      dot,
      cargo_insurance_provider: cargo_insurance_provider ?? "",
      cargo_insurance_value,
      about,
      language
    };

    await dispatch(updateUserProfile(userProfileData))
      .then(() => {
        showSuccessMessage("Successfully updated profile.");
      })
      .catch(() => {
        showErrorMessage("Error updating profile.");
      });
  };

  useEffect(() => {
    reset({
      user_type: account.type,
      email: email,
      owners_first_name: contact_name?.split(" ")[0],
      owners_last_name: contact_name?.split(" ")[1],
      contact_phone: contact_phone,
      fax_number: fax_number,
      url: url,
      dba: dba,
      addressData: address_object,
      mc: mc,
      dot: dot,
      cargo_insurance_provider: cargo_insurance_provider,
      cargo_insurance_value: cargo_insurance_value,
      about: about,
      language: defaultLanguage
    });
  }, [userDetails]);

  return (
    <MainLayout
      desktopTitle="Profile"
      customHeader={
        <Header
          label="Profile"
          headerIcon={<Icon path={mdiInformationOutline} />}
          goBackPath={routes.SETTINGS}
          isHideDesktop
        />
      }
      hideNavigation
      className="px-5 pb-6">
      <form className={styles.userProfileContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.profileCard}>
          <div className={styles.avatarWrapper}>
            <Icon path={mdiAccountOutline} className="m-auto size-10" />
          </div>
          <div className={styles.cardBody}>
            <h3 className={styles.contactName}>{contact_name}</h3>
            <div className={styles.detail}>
              <Icon path={mdiPhoneOutline} className="size-3 mr-1.5" />
              <span>
                {formatPhoneNumber(typeof contact_phone === "string" ? contact_phone : "")}
              </span>
            </div>
            <div className={styles.detail}>
              <Icon path={mdiEmailOutline} className="size-3 mr-1.5 flex-none" />
              <span className="break-all">{email}</span>
            </div>
          </div>
        </div>

        <div className={styles.profileForm}>
          <p className="px-2 text-sm text-placeholder">
            The settings below can be updated by sending a request to support@haulpay.io.
          </p>
          <Input
            label="&nbsp;&nbsp;Company Type"
            name="user_type"
            register={register}
            inputClass="capitalize"
            disabled
          />
          <Input
            type="email"
            name="email"
            label="&nbsp;&nbsp;Login Email"
            register={register}
            disabled
          />

          <div className={styles.ownerName}>
            <Input
              label="&nbsp;&nbsp;Company Contact First Name"
              name="owners_first_name"
              register={register}
              className="flex-1"
              disabled
            />
            <Input
              label="&nbsp;&nbsp;Company Contact Last Name"
              name="owners_last_name"
              className="flex-1"
              register={register}
              disabled
            />
          </div>

          <Controller
            name="contact_phone"
            control={control}
            render={({ field: { value = "", onChange } }) => (
              <PhoneNumber
                value={value}
                onChange={onChange}
                label="&nbsp;&nbsp;Phone Number Listed on Load Board"
                error={errors.contact_phone}
                wrapperClass="flex-1"
                disabled
              />
            )}
          />
          <Input
            label="&nbsp;&nbsp;Fax Listed on Load Board"
            name="fax_number"
            register={register}
            error={errors.fax_number}
            disabled
          />
          <Controller
            name="addressData"
            control={control}
            render={({ field: { value, onChange } }) => (
              <GoogleAutoComplete
                label="&nbsp;&nbsp;Business Address"
                value={value}
                onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
                disabled
              />
            )}
          />
          <Input type="url" label="&nbsp;&nbsp;Website" name="url" register={register} disabled />
          <Input label="&nbsp;&nbsp;Business Name" name="dba" register={register} disabled />
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;&nbsp;MC #"
            name="mc"
            register={register}
            error={errors.mc}
            disabled
          />
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;&nbsp;DOT #"
            name="dot"
            register={register}
            error={errors.dot}
            disabled
          />
          <Input
            label="&nbsp;&nbsp;Cargo Insurance Provider Listed on Load Board"
            name="cargo_insurance_provider"
            register={register}
            disabled
          />
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;&nbsp;Cargo Insurance Value Listed on Load Board"
            name="cargo_insurance_value"
            register={register}
            error={errors.cargo_insurance_value}
            disabled
          />
          <div className={styles.languagePreference}>
            <span className="text-sm text-text">Language preference?</span>

            <div className="flex gap-4">
              <Radio
                label="EN"
                name="language"
                value="en"
                onChange={(event) => setValue("language", event.target.value)}
                className="text-placeholder"
                checked={selectedLang === "en"}
                disabled
              />
              <Radio
                label="ES"
                name="language"
                value="es"
                onChange={(event) => setValue("language", event.target.value)}
                className="text-placeholder"
                checked={selectedLang === "es"}
                disabled
              />
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="flex-none desktop:!self-end mobile:flex-1 desktop:!w-56 desktop:!h-12"
            onClick={goProfile}>
            View Profile
          </Button>
        </div>
      </form>
    </MainLayout>
  );
}
